import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

const RULES = [
  { key: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "lo", label: "Lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { key: "up", label: "Uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "dg", label: "Digit (0–9)", test: (p) => /\d/.test(p) },
  { key: "sy", label: "Symbol (!@#$ …)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const forced = user?.password_change_required;
  const reason = user?.password_change_reason;

  useEffect(() => {
    if (user && !user.password_change_required && !window.location.search.includes("voluntary")) {
      // user landed here voluntarily — fine
    }
  }, [user]);

  const passed = RULES.map((r) => r.test(next));
  const allStrong = passed.every(Boolean);
  const matches = next && confirm && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allStrong) { toast.error("Password isn't strong enough yet"); return; }
    if (!matches) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const body = { new_password: next };
      if (!forced) body.current_password = current;
      await api.post("/auth/change-password", body);
      toast.success("Password updated");
      await refreshUser();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] grid place-items-center p-6" data-testid="change-password-page">
      <Card className="hr-card max-w-lg w-full p-8">
        <div className="flex items-center gap-3 mb-4">
          <img src={BRAND.logoUrl} alt="Solemnity" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display font-semibold text-[#0F172A] text-base">Update your password</div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400">Account security</div>
          </div>
        </div>

        {forced && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mb-5 flex gap-2" data-testid="forced-banner">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900">
              {reason === "first_login"
                ? "Welcome! For security, please set a new password before continuing."
                : `Your password is older than 30 days. Please choose a new one to keep your account secure.`}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!forced && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Current password</Label>
              <Input
                data-testid="current-password"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                className="mt-1.5 h-10"
                autoComplete="current-password"
              />
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">New password</Label>
            <div className="relative mt-1.5">
              <Input
                data-testid="new-password"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                className="h-10 pr-9"
                autoComplete="new-password"
              />
              <Lock className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <ul className="mt-2 space-y-1">
              {RULES.map((r, i) => (
                <li
                  key={r.key}
                  data-testid={`rule-${r.key}`}
                  className={`text-[11px] flex items-center gap-1.5 ${passed[i] ? "text-emerald-700" : "text-gray-400"}`}
                >
                  <CheckCircle2 className={`h-3 w-3 ${passed[i] ? "text-emerald-600" : "text-gray-300"}`} />
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Confirm new password</Label>
            <Input
              data-testid="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1.5 h-10"
              autoComplete="new-password"
            />
            {confirm && !matches && (
              <div className="text-[11px] text-red-600 mt-1">Passwords don&apos;t match.</div>
            )}
          </div>

          <Button
            data-testid="change-password-submit"
            type="submit"
            disabled={submitting || !allStrong || !matches}
            className="w-full h-11 bg-[#F26B3A] hover:bg-[#d85d2f]"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> :
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Save new password</span>}
          </Button>
        </form>
      </Card>
    </div>
  );
}
